import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import { Button } from '../../components/ui/button';
import ActionDropdown from '../../components/common/action-dropdown';
import PageHeader from '../../components/common/PageHeader';
import TradingStatusBadge from '../../components/trading/TradingStatusBadge';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import CopySettingsDialog, {
  CopySettings,
} from '../../components/trading/CopySettingsDialog';
import {
  DollarSign,
  TrendingUp,
  Pause,
  Play,
  Square,
  Settings,
  ArrowDownCircle,
  ArrowUpCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

type ActiveCopy = {
  id: string;
  traderName: string;
  strategyName: string;
  copiedAmount: number;
  currentProfit: number;
  roi: number;
  status: 'active' | 'paused' | 'stopped';
  copiedDate: string;
  lastSync: string;
  riskScore: number;
  minCopyAmount: number;
  commissionRate: number;
};

const defaultSettings: CopySettings = {
  copyAmount: '5000',
  allocationType: 'proportional',
  fixedLot: '0.10',
  riskMultiplier: '1.0',
  maxDrawdown: '20',
  maxDailyLoss: '100',
  maxOpenTrades: '5',
  copyOpenTrades: false,
  reverseCopy: false,
  slippagePips: '3',
  stopCopyLevel: '',
};

const MOCK_COPIES: ActiveCopy[] = [
  {
    id: 'COPY001',
    traderName: 'John Smith',
    strategyName: 'Gold Scalper Pro',
    copiedAmount: 5000,
    currentProfit: 750,
    roi: 15.0,
    status: 'active',
    copiedDate: '2024-01-15',
    lastSync: '2024-01-31',
    riskScore: 8,
    minCopyAmount: 100,
    commissionRate: 15,
  },
  {
    id: 'COPY002',
    traderName: 'Jane Doe',
    strategyName: 'Forex Trend Master',
    copiedAmount: 3000,
    currentProfit: 450,
    roi: 15.0,
    status: 'active',
    copiedDate: '2024-01-10',
    lastSync: '2024-01-31',
    riskScore: 5,
    minCopyAmount: 50,
    commissionRate: 12,
  },
  {
    id: 'COPY003',
    traderName: 'Bob Johnson',
    strategyName: 'Conservative Growth',
    copiedAmount: 2000,
    currentProfit: -100,
    roi: -5.0,
    status: 'paused',
    copiedDate: '2024-01-08',
    lastSync: '2024-01-25',
    riskScore: 3,
    minCopyAmount: 25,
    commissionRate: 8,
  },
];

const CopyTradingMyCopies = () => {
  const navigate = useNavigate();
  const [copies, setCopies] = useState<ActiveCopy[]>(MOCK_COPIES);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCopy, setSelectedCopy] = useState<ActiveCopy | null>(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [copySettings, setCopySettings] =
    useState<CopySettings>(defaultSettings);
  const [actionLoading, setActionLoading] = useState(false);

  const handlePauseResume = (copyId: string) => {
    setCopies(prev =>
      prev.map(c => {
        if (c.id === copyId) {
          const newStatus = c.status === 'active' ? 'paused' : 'active';
          toast.success(
            `Strategy ${c.strategyName} ${newStatus === 'paused' ? 'paused' : 'resumed'}`
          );
          return { ...c, status: newStatus };
        }
        return c;
      })
    );
  };

  const handleOpenSettings = (copy: ActiveCopy) => {
    setSelectedCopy(copy);
    setCopySettings({
      ...defaultSettings,
      copyAmount: String(copy.copiedAmount),
    });
    setShowSettingsDialog(true);
  };

  const handleOpenStopModal = (copy: ActiveCopy) => {
    setSelectedCopy(copy);
    setShowStopModal(true);
  };

  const handleStopCopy = async () => {
    if (!selectedCopy) return;
    setActionLoading(true);
    await new Promise(r => setTimeout(r, 600));
    setCopies(prev =>
      prev.map(c =>
        c.id === selectedCopy.id ? { ...c, status: 'stopped' } : c
      )
    );
    setActionLoading(false);
    setShowStopModal(false);
    toast.success(`Stopped copying ${selectedCopy.strategyName}`);
    setSelectedCopy(null);
  };

  const handleDeposit = (copy: ActiveCopy) => {
    navigate(`/payments/deposit?login=${copy.id}`);
  };

  const handleWithdraw = (copy: ActiveCopy) => {
    navigate(`/payments/withdrawal?login=${copy.id}`);
  };

  const columns: DataTableColumn<ActiveCopy>[] = [
    {
      id: 'traderName',
      header: 'Trader',
      accessor: row => row.traderName,
      sortable: true,
      width: '160px',
    },
    {
      id: 'strategyName',
      header: 'Strategy',
      accessor: row => (
        <span className="font-semibold">{row.strategyName}</span>
      ),
      sortable: true,
      width: '180px',
    },
    {
      id: 'copiedAmount',
      header: 'Copied Capital',
      accessor: row => `$${row.copiedAmount.toLocaleString()}`,
      sortable: true,
      width: '140px',
    },
    {
      id: 'currentProfit',
      header: 'Profit/Loss',
      accessor: row => (
        <span
          className={
            row.currentProfit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${row.currentProfit.toLocaleString()}
        </span>
      ),
      sortable: true,
      width: '140px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span
          className={
            row.roi >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      width: '100px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      width: '120px',
    },
    {
      id: 'copiedDate',
      header: 'Started Date',
      accessor: row => new Date(row.copiedDate).toLocaleDateString(),
      sortable: true,
      width: '130px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-1">
          {row.status !== 'stopped' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePauseResume(row.id)}
              title={row.status === 'active' ? 'Pause' : 'Resume'}
            >
              {row.status === 'active' ? (
                <Pause className="h-4 w-4 text-amber-600" />
              ) : (
                <Play className="h-4 w-4 text-green-600" />
              )}
            </Button>
          )}

          <ActionDropdown
            id={row.id}
            showView={false}
            showDelete={false}
            showEdit={false}
            additionalItems={
              <>
                {row.status !== 'stopped' && (
                  <button
                    className="flex items-center gap-2 px-2 py-1.5 text-xs text-foreground hover:bg-muted w-full text-left cursor-pointer"
                    onClick={() => handleOpenSettings(row)}
                  >
                    <Settings className="h-3.5 w-3.5" />
                    <span>Edit Settings</span>
                  </button>
                )}

                <button
                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-foreground hover:bg-muted w-full text-left cursor-pointer"
                  onClick={() => handleDeposit(row)}
                >
                  <ArrowDownCircle className="h-3.5 w-3.5 text-green-600" />
                  <span>Deposit Funds</span>
                </button>

                <button
                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-foreground hover:bg-muted w-full text-left cursor-pointer"
                  onClick={() => handleWithdraw(row)}
                >
                  <ArrowUpCircle className="h-3.5 w-3.5 text-blue-600" />
                  <span>Withdraw Funds</span>
                </button>

                {row.status !== 'stopped' && (
                  <button
                    className="flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 hover:bg-muted w-full text-left cursor-pointer border-t mt-1 pt-1.5"
                    onClick={() => handleOpenStopModal(row)}
                  >
                    <Square className="h-3.5 w-3.5" />
                    <span>Stop Copying</span>
                  </button>
                )}
              </>
            }
          />
        </div>
      ),
      width: '160px',
    },
  ];

  const filteredCopies = copies.filter(copy => {
    return (
      searchTerm === '' ||
      copy.traderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      copy.strategyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      copy.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalCopied = copies.reduce((sum, copy) => sum + copy.copiedAmount, 0);
  const totalProfit = copies.reduce((sum, copy) => sum + copy.currentProfit, 0);
  const activeCopies = copies.filter(copy => copy.status === 'active').length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Active Strategy Subscriptions"
          description="Manage, pause, resume, edit parameters, or deposit/withdraw funds from active copy subscriptions"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-950/40 border rounded-lg">
          <DollarSign className="h-6 w-6 text-blue-600" />
          <div>
            <p className="text-xs text-muted-foreground">
              Total Capital Allocated
            </p>
            <p className="text-2xl font-bold text-blue-600">
              ${totalCopied.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/40 border rounded-lg">
          <TrendingUp className="h-6 w-6 text-green-600" />
          <div>
            <p className="text-xs text-muted-foreground">
              Total Realized Profit
            </p>
            <p className="text-2xl font-bold text-green-600">
              ${totalProfit.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-purple-50 dark:bg-purple-950/40 border rounded-lg">
          <Play className="h-6 w-6 text-purple-600" />
          <div>
            <p className="text-xs text-muted-foreground">
              Active Subscriptions
            </p>
            <p className="text-2xl font-bold text-purple-600">{activeCopies}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-950/40 border rounded-lg">
          <TrendingUp className="h-6 w-6 text-orange-600" />
          <div>
            <p className="text-xs text-muted-foreground">Average Return</p>
            <p className="text-2xl font-bold text-orange-600">
              {copies.length > 0
                ? ((totalProfit / totalCopied) * 100).toFixed(2)
                : 0}
              %
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>My Copy Subscriptions</CardTitle>
          <CardDescription>
            Control active subscriptions, adjust risk, pause, or unsubscribe
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredCopies}
            searchPlaceholder="Search by trader, strategy, or subscription ID..."
            onSearchChange={setSearchTerm}
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>

      {selectedCopy && (
        <CopySettingsDialog
          open={showSettingsDialog}
          onOpenChange={setShowSettingsDialog}
          traderName={selectedCopy.traderName}
          strategyName={selectedCopy.strategyName}
          riskScore={selectedCopy.riskScore}
          minCopyAmount={selectedCopy.minCopyAmount}
          commissionRate={selectedCopy.commissionRate}
          settings={copySettings}
          onSettingsChange={setCopySettings}
          onConfirm={() => {
            setShowSettingsDialog(false);
            toast.success(`Updated settings for ${selectedCopy.strategyName}`);
          }}
        />
      )}

      <ConfirmationModal
        isOpen={showStopModal}
        title="Stop Copying Strategy"
        message={`Are you sure you want to stop copying ${selectedCopy?.strategyName}? All open positions for this strategy will be closed.`}
        confirmText="Stop & Close Trades"
        onConfirm={handleStopCopy}
        onCancel={() => setShowStopModal(false)}
        type="danger"
        loading={actionLoading}
      />
    </div>
  );
};

export default CopyTradingMyCopies;
